import { useTranslation } from "src/context/Translations/context";
import React, { useState, useRef } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import FiltersContainer from "src/components/FiltersContainer";
import { useForm } from "react-hook-form";
import LovUsers from "src/components/Lov/Users";
import Select from "src/components/Select";
import { standardErrorMessages } from "src/utils/errors";
import SortableContext from "src/components/Sortable/Context";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import DragOverlay from "src/components/Sortable/DragOverlay";
import SidebarCard from "src/components/SidebarCard";
import { useParams } from "react-router-dom";
import AppIcons from "src/components/AppIcons";
import { useAuth } from "src/context/Auth/context";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import LoadingContainer from "src/components/LoadingContainer";
import { IActionsMatrix, IFilters } from "./interface";
import { ActionSituation, IRefActionsPanelModalAddChangeDelete, IRefActionsProgressModal} from "../Panel/interface";
import CardActionsPrioritizationMatrix from "./components/Card";
import MatrixPriority from "./components/Matrix";
import { WrapNewActions } from "./components/Matrix/styles";
import ModalsSubactions, { IRefModalSubaction } from "../Panel/components/modalsSubactions";
import ModalPostponement, { IActionPostponementRef } from "../Panel/components/modalPostponement";
import ModalCancel, { IActionCancelRef } from "../Panel/components/modalCancel";
import ModalAddChangeDelete from "../Panel/components/modalAddChangeDelete";
import ModalProgress from "../Panel/components/modalProgress";
import { PrioritizationMatrixContext } from "./prioritizationMatrixContext";
import ListOfCardMatrix from "./components/Card/listOfCard";

const PrioritizationMatrix: React.FC = () => {
	const { translate } = useTranslation();
	const { user } = useAuth();
	const { addToast } = RCCHookToast();
	const [showFilters, setShowFilters] = useState(false);
	const params = useParams<{
        responsible: string;
    }>();

	const modalsActionsRef = useRef<IRefActionsPanelModalAddChangeDelete>(null);
	const modalsSubactionsRef = useRef<IRefModalSubaction>(null);
	const modalProgressRef = useRef<IRefActionsProgressModal>(null);
	const modalPostponementRef = useRef<IActionPostponementRef>(null);
	const modalCancelRef = useRef<IActionCancelRef>(null);

	const [actions, setActions] = useState<IActionsMatrix[]>([]);
	const [newitems, setNewItems] = useState<IActionsMatrix[]>([]);
	const [container1, setContainer1] = useState<IActionsMatrix[]>([]);
	const [container2, setContainer2] = useState<IActionsMatrix[]>([]);
	const [container3, setContainer3] = useState<IActionsMatrix[]>([]);
	const [container4, setContainer4] = useState<IActionsMatrix[]>([]);

	const [actualContainer, setActualContainer] = useState<string | null>(null);

	const [activeCard, setActiveCard] = useState<IActionsMatrix | null>(null);

	const { 
		handleSubmit : handleSubmitFilters, 
		control:controlFilters, 
		reset: resetFilters 
	} = useForm();

	const [filters, setFilters] = useState<IFilters>({ 
		late:  "",
		responsable: !params.responsible || params.responsible === null || params.responsible.trim().length === 0 ? {
			value: user.efa005Id.toString(), 
			label: user.efa005Nome
		} :  {
			value: params.responsible.split("_")[0], 
			label: params.responsible.split("_")[1]
		} ,
		situation: [{
			label: translate("Em andamento"),
			value: ActionSituation.IN_PROGRESS
		},{
			label: translate("Não Iniciada"),
			value: ActionSituation.NOT_STARTED
		}]
	});

	async function filtersMatrix(data: any){
		setFilters({
			late: data.late,
			responsable: data.responsable,
			situation: data.situation
		});
	}

	const objToString = (obj: any) => {
		if(typeof obj === "object")
			return obj.value;
		if(typeof obj === "number")
			return obj.toString();
		if(typeof obj !== "string")
			return "";
		return obj;
	};

	const { errorFetching, loading, loadData } = useEffectFetchData<{
		newItems: IActionsMatrix[],
		container1: IActionsMatrix[],
		container2: IActionsMatrix[],
		container3: IActionsMatrix[],
		container4: IActionsMatrix[]
	}> (
		"Actions/ListEisenhowerMatrix", 
		{ 
			responsable: objToString(filters.responsable) ?? "",
			late: objToString(filters.late) && objToString(filters.late) !== "T" ? objToString(filters.late) : "",
			situation: filters.situation && filters.situation.length > 0 ? extractSingleValueFromArrayOfObject("value", filters.situation): ""
		},
		({ data }) => {
				
			setNewItems(data.newItems);
			setContainer1(data.container1);
			setContainer2(data.container2);
			setContainer3(data.container3);
			setContainer4(data.container4);
				
			setActions([
				...data.newItems,
				...data.container1,
				...data.container2,
				...data.container3,
				...data.container4
			]);

		},
		[filters]
	);


	const queryUpdateActions = async (actionId: number, importance: number, urgency: number) => {
		try{
			await RCCServicesApi.postRequest({
				method: "Actions/UpdatePrioritiesMatrix",
				urlParameters: {
					actionId,
					importance: importance === -1 ? "": importance,
					urgency: urgency === -1 ? "": urgency
				}
			});
			return true;
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar ação"), addToast);
			return false;
		}
	};

	function findContainer(id: number | string) {
		let idConverted = id;
		if(typeof id === "string")
			idConverted = parseInt(id, 10);	
		if(newitems.findIndex((act) => act.inb001Id === idConverted) !== -1)
			return "0";
		if(container1.findIndex((act) => act.inb001Id === idConverted) !== -1)
			return "1";
		if(container2.findIndex((act) => act.inb001Id === idConverted) !== -1)
			return "2";
		if(container3.findIndex((act) => act.inb001Id === idConverted) !== -1)
			return "3";
		if(container4.findIndex((act) => act.inb001Id === idConverted) !== -1)
			return "4";

		return "-1";
	}

	function returnElementsOfContainer(nameOfContainer: string){
		if(nameOfContainer === "0")
			return newitems;
		if(nameOfContainer === "1")
			return container1;
		if(nameOfContainer === "2")
			return container2;
		if(nameOfContainer === "3")
			return container3;
		if(nameOfContainer === "4")
			return container4;
		return [];
	}

	const updateState = async (id: number, remove: boolean, container: string) => {
		if(container === "0"){
			if(remove){
				setNewItems(newitems.filter((x) => x.inb001Id !== id));
				return;
			}	
			const item = actions.find((x) => x.inb001Id === id);
			if(!item) return;
			const tempItems = newitems;
			tempItems.push(item);
			setNewItems(tempItems);
		}else if(container === "1"){
			if(remove){
				setContainer1(container1.filter((x) => x.inb001Id !== id));
				return;
			}		
			const item = actions.find((x) => x.inb001Id === id);
			if(!item) return;
			const tempItems = container1;
			tempItems.push(item);
			setContainer1(tempItems);
		}else if(container === "2"){
			if(remove){
				setContainer2(container2.filter((x) => x.inb001Id !== id));
				return;
			}	
			const item = actions.find((x) => x.inb001Id === id);
			if(!item) return;
			const tempItems = container2;
			tempItems.push(item);
			setContainer2(tempItems);
	
		}else if(container === "3"){
			if(remove){
				setContainer3(container3.filter((x) => x.inb001Id !== id));
				return;
			}		
			const item = actions.find((x) => x.inb001Id === id);
			if(!item) return;
			const tempItems = container3;
			tempItems.push(item);
			setContainer3(tempItems);
	
		}else if(container === "4"){
			if(remove){
				setContainer4(container4.filter((x) => x.inb001Id !== id));
				return;
			}
			const item = actions.find((x) => x.inb001Id === id);
			if(!item) return;
			const tempItems = container4;
			tempItems.push(item);
			setContainer4(tempItems);
		}
	};

	const diferentContainer = (overcContainer: string, activeId: string, activeElement: IActionsMatrix) => {
		let importancy = 0;
		let urgency = 0;

		if(overcContainer === "0")
			return;

		switch (overcContainer) {
		case "1":
			importancy = 10;
			urgency = 10;
			break;
		case "2":
			importancy = 10;
			urgency = 0;
			break;
		case "3":
			importancy = 0;
			urgency = 10;
			break;
		case "4":
			importancy = 0;
			urgency = 0;
			break;
		default:
			importancy = -1;
			urgency = -1;
			break;
		}
		queryUpdateActions(parseInt(activeId, 10), importancy, urgency);
		const newActiveElement = activeElement;
		newActiveElement.inb001Importancia = importancy;
		newActiveElement.inb001Urgencia = urgency;
		setActions((prev) => [
			...prev, 
			newActiveElement
		]);
	};

	const handleDragEnd = (activeId: string, overId: string) => {
		const activeContainer = findContainer(activeId);
		const activeElement = actions.find((act) => act.inb001Id === parseInt(activeId, 10));
		if(!activeElement || !activeContainer || overId === "0")
			return;
		if(typeof overId === "string"){	
		
			diferentContainer(overId, activeId, activeElement);
		} else{
			const overContainer = findContainer(overId);

			if(activeContainer === overContainer && activeContainer === actualContainer){
				const activeItems = returnElementsOfContainer(activeContainer);
				const overItems = returnElementsOfContainer(overContainer);

				const activeIndex = activeItems.findIndex((objective: any) => objective.id === activeId);
				const overIndex = overItems.findIndex((objective: any) => objective.id === overId);

				const temp = arrayMove(activeItems, 
					activeIndex, 
					overIndex
				);
				if(activeContainer === "0"){
					setNewItems(temp);
					return;
				}
				if(activeContainer === "1"){
					setContainer1(temp);
					return;
				}
				if(activeContainer === "2"){
					setContainer2(temp);
					return;
				}
				if(activeContainer === "3"){
					setContainer3(temp);
					return;
				}
				if(activeContainer === "4"){
					setContainer4(temp);
				}
			}else if(actualContainer){
				diferentContainer(overContainer, activeId, activeElement);
			}	
		}

		loadData();
		setActiveCard(null);
	};

	const handleDragOver = async (activeId: string, overId: string) => {
		if(typeof overId === "string"){	
			const activeContainer = findContainer(activeId);
			if(activeContainer === overId || overId === "0")
				return;
			if(activeContainer !== "-1"){
				await updateState(parseInt(activeId, 10), true, activeContainer);
				await updateState(parseInt(activeId, 10), false, overId);
			}
		}	
	};

	const handleDragStart = (activeId: string) => {
		const element = actions.find((elem) => elem.id === activeId);
		if(!element) return;
		const container = findContainer(element.id);
		setActualContainer(container);
		setActiveCard(element);	
	};


	return (
		<>
			<PrioritizationMatrixContext.Provider
				value={{
					onLoad: loadData,
					openModalActionAddChange: modalsActionsRef.current?.openModalAddOrChange,
					openModalActionChangeProgress: modalProgressRef.current?.openModalChangeProgress,
					openModalActionPostponement: modalPostponementRef.current?.openModalActionPostponement,
					openModalActionCancel: modalCancelRef.current?.openModalActionCancel,
					openModalActionDelete: modalsActionsRef.current?.actionButtonDelete
				}}
			>
				<RCCGrid
					container
					item
					xs={12}
					sm={12} 
				>
					<SortableContext 
						onDragEnd={(activeId, overId) => handleDragEnd(activeId, overId)}
						onDragOver={(activeId, overId) => handleDragOver(activeId, overId)}
						onDragStart={(activeId) => handleDragStart(activeId)}
					>
						<SidebarCard 
							icon={() => <AppIcons name="actions" />}
							title={translate("Matriz de Priorização")}
							buttonsContent={
								<RCCTooltip
									text={translate("Filtros")}
									color={showFilters || 
												objToString(filters.late)  !== "T" || 
												objToString(filters.responsable) || 
												filters.situation && filters.situation.length > 0 ? "blue" : "grey"}
								>
									<RCCButton
										icon={{name: "filter", color: "#FFF", size: 16}}
										color={showFilters || 
												objToString(filters.late)  !== "T" || 
												objToString(filters.responsable) || 
												filters.situation && filters.situation.length > 0 ? "blue" : "grey"}
										layout="isRounded"
										size="md"
										onClick={() => setShowFilters((prevState) => !prevState)}
									/>
								</RCCTooltip>
							}
							filterContent={
								<FiltersContainer
									onSubmitFilters={handleSubmitFilters(filtersMatrix)}
									resetFilters={() => resetFilters({ late: {value: "T", label: translate("Todos")}, responsable: {value: "", label: ""}, situation: null })}
									showFilters={showFilters}
								>
									<RCCGrid
										container
										item
									>
										<RCCGrid
											container 
											item
											xs={12}
										>
											<LovUsers 
												name="responsable"
												control={controlFilters}
												label={translate("Responsável")}
												defaultValue={filters.responsable}
												isClearable
											/>
										</RCCGrid>
										<RCCGrid
											container 
											item
											xs={12}
										>
											<Select 
												control={controlFilters}
												name="situation"
												label={translate("Situação")}
												defaultValue={filters.situation || [{
													label: translate("Concluída"),
													value: ActionSituation.FINISHED
												},{
													label: translate("Em andamento"),
													value: ActionSituation.IN_PROGRESS
												},{
													label: translate("Não Iniciada"),
													value: ActionSituation.NOT_STARTED
												}]}
												options={[{
													label: translate("Cancelada"),
													value: ActionSituation.CANCELED
												},{
													label: translate("Em Rascunho"),
													value: ActionSituation.DRAFT
												},{
													label: translate("Concluída"),
													value: ActionSituation.FINISHED
												},{
													label: translate("Em andamento"),
													value: ActionSituation.IN_PROGRESS
												},{
													label: translate("Não Iniciada"),
													value: ActionSituation.NOT_STARTED
												}]}
												isMulti
											/>
										</RCCGrid>
										<RCCGrid
											container 
											item
											xs={12}
										>
											<Select 
												control={controlFilters}
												name="late"
												label={translate("Atrasado")}
												options={[{
													label: translate("Não"),
													value: "1"
												},
												{
													label: translate("Sim"),
													value: "0"
												},
												{
													label: translate("Todos"),
													value: "T"
												}]}
												defaultValue={filters.late ? filters.late : { value: "T", label: translate("Todos") }}
												submit="value and label"
											/>
										</RCCGrid>
									</RCCGrid>
								</FiltersContainer>}
							customCards={
								<WrapNewActions filtersOpen={showFilters}>
									<ListOfCardMatrix 
										data={newitems}
										number="0"
										numberOfElements={15}
									/>
								</WrapNewActions>}
							content={<>
								{errorFetching ? (
									<RCCAlert 
										color="red"
										title={translate("Atenção")}
										description={translate("Erro ao carregar dados")}
									/>
								): 
									<>
										<LoadingContainer loading={loading}>
											<MatrixPriority 
												container1={container1}
												container2={container2}
												container3={container3}
												container4={container4}
											/>	
											<div style={{ listStyle: "none" }}>
												<DragOverlay>
													{activeCard ? (
														<CardActionsPrioritizationMatrix
															data={activeCard}
														/>
													): null}
												</DragOverlay>
											</div>
										</LoadingContainer>	
									</>
								}
										
							
							</>}
						/>
					</SortableContext>
				</RCCGrid>

			</PrioritizationMatrixContext.Provider>

			<ModalsSubactions
				ref={modalsSubactionsRef} 
				onCallbackClose={() => loadData()}
			/>

			<ModalAddChangeDelete 
				ref={modalsActionsRef} 
				onCallbackDelete={() => loadData()}
			/>

			<ModalProgress ref={modalProgressRef} />

			<ModalPostponement ref={modalPostponementRef} />

			<ModalCancel ref={modalCancelRef} />
		</>
	);
};

export default PrioritizationMatrix;
